import React, { useState, useEffect } from 'react';
import validationAuth from '../../validationAuth';
import {
    Container, Table, TableBody, TableCell, TableRow, Paper, Box, Typography, Grid,
    IconButton, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogContent, DialogTitle, TableContainer, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { format, getMonth, setMonth } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import * as common from '../../commons/common';
import useScrolling from './useScrolling';
import leftArrowIcon from '../../images/left_arrow_icon.svg';

function CustomCalendar({ value, onChange, abnormalDates, onMonthChange }) {
    const [currentDate, setCurrentDate] = useState(value);

    const renderHeader = () => {
        const dateFormat = 'yyyy년 MM월';

        return (
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <IconButton
                    aria-label="previous month"
                    onClick={prevMonth}
                    size="small"
                    sx={{ backgroundColor: 'transparent' }}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h6" component="div">
                    {format(currentDate, dateFormat, { locale: ko })}
                </Typography>
                <IconButton
                    aria-label="next month"
                    onClick={nextMonth}
                    size="small"
                    sx={{ backgroundColor: 'transparent' }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        );
    };

    const renderDays = () => {
        const dateFormat = 'E';
        const days = [];

        let startDate = new Date(currentDate);
        startDate.setDate(1);
        startDate.setDate(startDate.getDate() - startDate.getDay() + 1);

        for (let i = 0; i < 7; i++) {
            days.push(
                <Box key={i} sx={{
                    width: '34px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                }}>
                    {format(startDate, dateFormat, { locale: ko })}
                </Box>
            );
            startDate.setDate(startDate.getDate() + 1);
        }

        return <Box display="flex" justifyContent="space-evenly" mb={2}>{days}</Box>;
    };

    const renderCells = () => {
        const monthStart = new Date(currentDate);
        monthStart.setDate(1);

        const monthEnd = new Date(currentDate);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(0);

        const startDate = new Date(monthStart);
        startDate.setDate(startDate.getDate() - startDate.getDay() + 1);

        const endDate = new Date(monthEnd);
        endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

        const rows = [];
        let days = [];
        let day = new Date(startDate);
        let formattedDate = '';

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                formattedDate = format(day, 'd', { locale: ko });
                const cloneDay = new Date(day);
                const isAbnormal = abnormalDates.some(date => isSameDay(date, cloneDay));

                days.push(
                    <Box
                        key={day.toString()}
                        onClick={() => onDateClick(cloneDay)}
                        sx={{
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            backgroundColor: isSameDay(day, value) ? 'var(--sub-orange-color)' : 'transparent',
                            color: isSameDay(day, value) ? '#fff' : isSameMonth(day, currentDate) ? '#000' : '#ccc',
                            border: 'none',
                            borderRadius: '50%',
                            position: 'relative'
                        }}
                    >
                        {formattedDate}
                        {isAbnormal && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    border: '3px solid #FF6347',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isSameDay(day, value) ? '#FFAB40' : 'transparent',
                                }}
                            >
                                {formattedDate}
                            </Box>
                        )}
                    </Box>
                );
                day.setDate(day.getDate() + 1);
            }
            rows.push(<Box key={day.toString()} display="flex" justifyContent="space-evenly" mb={1}>{days}</Box>);
            days = [];
        }

        return <Box>{rows}</Box>;
    };

    const prevMonth = () => {
        const newDate = setMonth(currentDate, getMonth(currentDate) - 1);
        setCurrentDate(newDate);
        if (onMonthChange) onMonthChange(newDate);
    };

    const nextMonth = () => {
        const newDate = setMonth(currentDate, getMonth(currentDate) + 1);
        setCurrentDate(newDate);
        if (onMonthChange) onMonthChange(newDate);
    };

    const onDateClick = (day) => {
        setCurrentDate(day);
        onChange(day);
    };

    const isSameDay = (d1, d2) => {
        return d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear();
    };

    const isSameMonth = (d1, d2) => {
        return d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
    };

    return (
        <Box>
            {renderHeader()}
            {renderDays()}
            {renderCells()}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: '3px solid #FF6347',
                    marginRight: '8px'
                }} />
                <Typography sx={{ marginRight: 2 }}>이상</Typography>
            </Box>
        </Box>
    );
}

function RegisteredDetail() {
    const isScrolling = useScrolling();  //scroll 상태
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [abnormalDates, setAbnormalDates] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const [checkRsltList, setCheckRsltList] = useState([]); // 날짜별 점검 리스트(상세x)
    const [expandedDetail, setExpandedDetail] = useState(false); //점검데이터 상세 펼치기
    const { facilityId, facilityName, facilityTypeNm } = location.state || {};

    useEffect(() => {
        if (facilityId) {
            fetchCheckRsltList(selectedDate);
        }
    }, [facilityId]);

    // 날짜별 점검 리스트 가져오기
    const fetchCheckRsltList = async (date) => {
        const url = `${common.getApiUrl()}/checker/result/${facilityId}`;
        const token = localStorage.getItem('access_token');

        try {
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: {
                    checkTime: format(date, 'yyyy-MM-dd'),
                },
            });
            const updatedcheckRsltList = response.data
                .map((item) => ({
                    checkRsltMstrId: item.checkRsltMstrId,
                    checkTime: item.checkTime,
                    checkerId: item.checkerId,
                    checkerNm: item.checkerNm,
                    selectedValue: {},
                }))
                .sort((a, b) => new Date(b.date) - new Date(a.date));

            setCheckRsltList(updatedcheckRsltList);
        } catch (error) {
            common.handleApiError(error);
        }
    };

    // 월별 이상 점검 날짜 계산
    const fetchMonthAbnormalDates = async (month) => {
        const token = localStorage.getItem('access_token');
        try {
            // 1. 해당 월의 점검 날짜 목록 가져오기
            const datesResponse = await axios.get(
                `${common.getApiUrl()}/checker/result-time/${facilityId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { month: format(month, 'yyyyMM') },
                }
            );

            const checkDates = datesResponse.data.checkTime || [];
            if (checkDates.length === 0) {
                setAbnormalDates([]);
                return;
            }

            // 고유 날짜 추출
            const uniqueDates = [...new Set(
                checkDates.map(d => format(new Date(d.replace(' ', 'T')), 'yyyy-MM-dd'))
            )];

            // 2. 각 날짜별 점검 목록 병렬 조회
            const listResponses = await Promise.all(
                uniqueDates.map(date =>
                    axios.get(`${common.getApiUrl()}/checker/result/${facilityId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { checkTime: date },
                    })
                )
            );

            // 모든 점검 레코드 수집
            const allRecords = listResponses.flatMap((r, i) =>
                r.data.map(item => ({ checkRsltMstrId: item.checkRsltMstrId, checkTime: item.checkTime }))
            );

            if (allRecords.length === 0) {
                setAbnormalDates([]);
                return;
            }

            // 3. 각 점검 상세 병렬 조회
            const detailResponses = await Promise.all(
                allRecords.map(({ checkRsltMstrId }) =>
                    axios.get(`${common.getApiUrl()}/checker/result/detail/${checkRsltMstrId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    })
                )
            );

            // 4. 이상 여부 판정 후 날짜 수집
            const newAbnormalDates = [];
            detailResponses.forEach((res, idx) => {
                const details = res.data.checkRsltDtlRes || [];
                const mstrRm = details.length > 0 ? details[0].mstrRm || '' : '';

                const isAbnormal =
                    details.some(item => item.textYn !== 'Y' && item.checkRsltValNm && item.checkRsltValNm !== '정상') ||
                    details.some(item => item.textYn === 'Y' && item.checkRsltValNm && item.checkRsltValNm.trim() !== '') ||
                    !!(mstrRm && mstrRm.trim() !== '');

                if (isAbnormal) {
                    const checkDate = new Date(allRecords[idx].checkTime.replace(' ', 'T'));
                    const alreadyExists = newAbnormalDates.some(d =>
                        d.getDate() === checkDate.getDate() &&
                        d.getMonth() === checkDate.getMonth() &&
                        d.getFullYear() === checkDate.getFullYear()
                    );
                    if (!alreadyExists) newAbnormalDates.push(checkDate);
                }
            });

            setAbnormalDates(newAbnormalDates);
        } catch (error) {
            common.handleApiError(error);
        }
    };

    // 날짜 선택
    const handleDateChange = (date) => {
        setSelectedDate(date);
        fetchCheckRsltList(date);
        setOpenDatePicker(false);
    };

    const handleOpenDatePicker = () => {
        setOpenDatePicker(true);
        fetchMonthAbnormalDates(selectedDate);
    };

    // 아코디언 선택
    const handleAccordionChange = async (checkRsltMstrId) => {
        await fetchAccordionDetails(checkRsltMstrId);
        setExpandedDetail((prevExpanded) =>
            prevExpanded === checkRsltMstrId ? false : checkRsltMstrId
        );
    };

    // 아코디언 상세 정보 가져오기
    const fetchAccordionDetails = async (checkRsltMstrId) => {
        const url = `${common.getApiUrl()}/checker/result/detail/${checkRsltMstrId}`;
        const token = localStorage.getItem('access_token');

        try {
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            // 비고(mstrRm) 값 추출 - 첫 번째 항목에서 가져오기
            let mstrRm = '';
            if (response.data.checkRsltDtlRes && response.data.checkRsltDtlRes.length > 0) {
                mstrRm = response.data.checkRsltDtlRes[0].mstrRm || '';
            }

            //점검유형 매핑
            const groupedData = response.data.checkRsltDtlRes.reduce((acc, item) => {
                if (!acc[item.checkTypeId]) {
                    acc[item.checkTypeId] = [];
                }
                acc[item.checkTypeId].push(item);
                return acc;
            }, {});

            // 이상 여부 체크
            const isAbnormal =
                // 라디오 항목 중 '정상' 외 다른 값이 하나라도 있으면 이상
                response.data.checkRsltDtlRes.some(
                    item => item.textYn !== 'Y' && item.checkRsltValNm && item.checkRsltValNm !== '정상'
                ) ||
                // 텍스트 입력 항목에 내용이 있으면 이상
                response.data.checkRsltDtlRes.some(
                    item => item.textYn === 'Y' && item.checkRsltValNm && item.checkRsltValNm.trim() !== ''
                ) ||
                // 비고에 내용이 있으면 이상
                !!(mstrRm && mstrRm.trim() !== '');

            setCheckRsltList((prevData) => {
                const newData = [...prevData];

                const index = newData.findIndex(item => item.checkRsltMstrId === checkRsltMstrId);

                if (index !== -1) {
                    // 이상 점검인 경우 해당 날짜를 abnormalDates에 추가
                    if (isAbnormal) {
                        const checkDate = new Date(newData[index].checkTime.replace(' ', 'T'));
                        setAbnormalDates(prev => {
                            const alreadyExists = prev.some(d =>
                                d.getDate() === checkDate.getDate() &&
                                d.getMonth() === checkDate.getMonth() &&
                                d.getFullYear() === checkDate.getFullYear()
                            );
                            return alreadyExists ? prev : [...prev, checkDate];
                        });
                    }
                    // 해당 인덱스의 객체를 업데이트
                    newData[index] = {
                        ...newData[index],
                        selectedValue: groupedData,
                        checkRsltDtls: response.data,
                        mstrRm: mstrRm  // 추출한 비고 값 설정
                    };
                } else {
                    console.error('해당 점검데이터를 찾을 수 없습니다.');
                }

                return newData;
            });

        } catch (error) {
            common.handleApiError(error);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            backgroundColor: 'var(--main-white-color)',
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
                width: '100%',
                backgroundColor: 'var(--main-blue-color)',
                // height: isScrolling ? '60px' : '160px', // 스크롤 할때 크기
                height: '60px',
                position: 'fixed',
                top: 0,
                zIndex: 1100,
                transition: 'height 0.2s ease'
            }}>
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'var(--main-blue-color)',
                    zIndex: 1100,
                }}>
                    {/* 이전 버튼 */}
                    <Box sx={{
                        position: 'absolute',
                        left: '1rem',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        backgroundColor: '#fff',
                        borderRadius: '15px',
                        padding: '0 12px 0 8px'
                    }}
                         onClick={() => navigate(-1)}>
                        <IconButton sx={{ padding: '4px'}}>
                            <img src={leftArrowIcon} alt="뒤로가기" style={{ width: '15px', height: '15px' }} />
                        </IconButton>
                    </Box>

                    {/* 시설명 */}
                    <Typography variant="h4" sx={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        maxWidth: 'calc(100% - 140px)', // 양쪽 버튼 공간 확보
                        fontSize: isScrolling ? '1.2rem' : '1.8rem',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#fff',
                        zIndex: 1
                    }}>
                        {facilityName}
                    </Typography>
                </Box>
            </Box>
            <Container maxWidth="false" sx={{
                width: '100%',
                mx: 'auto',
                mb: 6,
                // marginTop: isScrolling ? '120px' : '170px',
                marginTop: '5rem',
            }}>
                <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ mb: '1rem' }}>
                    <Grid item xs={12} md={6}>
                        <Box
                            onClick={handleOpenDatePicker}
                            sx={{
                                display: 'flex',
                                height: '50px',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px',
                                borderRadius: '20px',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                            }}
                        >
                            <Typography sx={{ paddingLeft: '10px'}}>
                                {format(selectedDate, 'yyyy년 MM월 dd일')}
                            </Typography>
                            <IconButton
                                disableRipple
                                size="small"
                                sx={{
                                    padding: '8px',
                                    '&:hover': {
                                        backgroundColor: 'transparent'
                                    }
                                }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: '1.8rem', color: '#A9A9A9' }} />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>
                {checkRsltList.length > 0 ? (
                    checkRsltList.map((checkRslt) => (
                        <Accordion
                            disableGutters
                            key={checkRslt.checkRsltMstrId}
                            expanded={expandedDetail === checkRslt.checkRsltMstrId}
                            onChange={() => handleAccordionChange(checkRslt.checkRsltMstrId)}
                            elevation={0}
                            sx={{
                                marginBottom: '1rem',
                                background: 'transparent',
                                '&::before': {
                                    display: 'none',
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.8rem', color: '#A9A9A9' }} />}
                                sx={{
                                    minHeight: '48px',
                                    height: '48px',
                                    padding: '0 16px',
                                    backgroundColor: 'white',
                                    borderRadius: '15px',
                                    //marginBottom: expandedDetail === checkRslt.checkRsltMstrId ? '1rem' : '0',
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        height: '48px',
                                    },
                                    '.MuiAccordionSummary-content': {
                                        margin: 0,
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        padding: '8px 0',
                                    }}
                                >
                                    <Typography
                                        sx={{ fontSize: '1.0rem', }}
                                    >
                                        {checkRslt.checkTime
                                            ? format(
                                                new Date(checkRslt.checkTime.replace(' ', 'T')),
                                                'a h시 mm분',
                                                { locale: ko }
                                            )
                                            : ''}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{
                                padding: '0px',
                                backgroundColor: 'transparent',
                                marginTop: '0'
                            }}>
                                {checkRslt.selectedValue && Object.entries(checkRslt.selectedValue).map(([checkTypeId, items], idx, arr) => (
                                    <Box
                                        key={checkTypeId}
                                        sx={{
                                            backgroundColor: 'white',
                                            margin: '1rem 0',
                                            overflow: 'hidden',
                                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                                            borderRadius: '15px 15px 15px 15px'
                                        }}
                                    >
                                        {arr.length > 0 && (
                                            <Box sx={{
                                                backgroundColor: '#E7E7E7',
                                                padding: '8px 16px',
                                                borderRadius: '15px 15px 0 0'
                                            }}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {items[0].checkTypeNm}
                                                </Typography>
                                            </Box>
                                        )}
                                        <TableContainer sx={{
                                            padding: '12px',
                                            margin: '0px',
                                            width: '100%',
                                            overflow: 'hidden',
                                            backgroundColor: 'white'
                                        }}>
                                            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                                                <TableBody>
                                                    {items.map((item, itemIndex) => (
                                                        <React.Fragment key={item.checkItemId}>
                                                            {itemIndex !== 0 && (
                                                                <TableRow>
                                                                    <TableCell sx={{ padding: 0, border: 'none' }}>
                                                                        <Box sx={{
                                                                            height: '1px',
                                                                            backgroundColor: 'var(--main-white-color)',
                                                                            mx: 2,
                                                                        }}></Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                            <TableRow sx={{ width: '100%' }}>
                                                                <TableCell sx={{
                                                                    //fontWeight: 'bold',
                                                                    fontSize: '1rem',
                                                                    border: 'none',
                                                                    color: '#505050',
                                                                    paddingX: 2,
                                                                    paddingTop: 1,
                                                                    paddingBottom: 1,
                                                                    width: '100%'
                                                                }}>
                                                                    <Box sx={{ display: 'flex', width: '100%', flexWrap: 'wrap' }}>
                                                                        <Box sx={{ width: '35%', flexShrink: 0 }}>
                                                                            {item.checkItemNm}
                                                                        </Box>
                                                                        <Box sx={{ width: '65%', flexGrow: 1 }}>
                                                                            <Box sx={{display:'flex', flexDirection:'column'}}>
                                                                                <Box sx={{
                                                                                    padding: '4px 8px',
                                                                                    backgroundColor: 'var(--main-white-color)',
                                                                                    color: '#3B62A7',
                                                                                    fontSize: '0.9rem',
                                                                                    overflowWrap: 'break-word',
                                                                                }}>
                                                                                    {item.checkRsltValNm || ''}
                                                                                </Box>
                                                                                <Box sx={{
                                                                                    padding: '4px 8px',
                                                                                    fontSize: '0.8rem',
                                                                                    overflowWrap: 'break-word',
                                                                                }}>
                                                                                    특이사항 : {item.rm || ''}
                                                                                </Box>
                                                                                <Box sx={{ marginTop: 1, display: 'flex', }}>
                                                                                    {item.dtlUploadFiles.map((filePath, index) => {
                                                                                        const fileExtension = filePath.split('.').pop().toLowerCase();
                                                                                        const isImage = ['png', 'jpg', 'jpeg', 'gif'].includes(fileExtension);
                                                                                        const isVideo = ['mp4', 'mov', 'avi'].includes(fileExtension);

                                                                                        return (
                                                                                            <div key={index} style={{ marginRight: '10px' }}>
                                                                                                {isImage ? (
                                                                                                    <img
                                                                                                        src={common.getImageBaseUrl() + filePath}
                                                                                                        alt="미리보기"
                                                                                                        style={{ width: '85px', height: 'auto', borderRadius: '4px' }}
                                                                                                    />
                                                                                                ) : (
                                                                                                    <video
                                                                                                        src={common.getImageBaseUrl() + filePath}
                                                                                                        controls
                                                                                                        style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                                                                                                    />
                                                                                                )}
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </Box>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        </React.Fragment>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                ))}
                                {/* 마스터 비고 */}
                                {checkRslt.mstrRm && (
                                    <Box
                                        sx={{
                                            backgroundColor: 'white',
                                            //margin: '10px 10px',
                                            overflow: 'hidden',
                                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                                            borderRadius: '15px'
                                        }}
                                    >
                                        <Box sx={{
                                            backgroundColor: '#E7E7E7',
                                            padding: '8px 16px',
                                        }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                ▣ 비고
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            padding: '16px 28px',
                                            fontSize: '1.0rem',
                                            overflowWrap: 'break-word',
                                            fontFamily: '"Pretendard-Regular", "Apple Color Emoji", "Noto Sans KR"',
                                        }}>
                                            {checkRslt.mstrRm}
                                        </Box>
                                    </Box>
                                )}
                                {/* 수정하기 버튼 */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate(`/checker/register-check/${facilityId}`, {
                                            state: { checkRsltMstrId: checkRslt.checkRsltMstrId, facilityName }
                                        })}
                                        sx={{
                                            backgroundColor: 'var(--main-blue-color)',
                                            color: 'white',
                                            borderRadius: '16px',
                                            padding: '4px 16px',
                                            fontSize: '1.0rem',
                                            minWidth: '100px',
                                            boxShadow: 'none',
                                            '&:hover': { backgroundColor: 'var(--main-blue-color)', boxShadow: 'none' },
                                        }}
                                    >
                                        수정하기
                                    </Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))
                ) : (
                    <Paper elevation={0} sx={{ padding: '16px', marginBottom: '16px' }}>
                        <Typography variant="body1" align="center">
                            선택한 날짜에 점검 기록이 없습니다.
                        </Typography>
                    </Paper>
                )}
                {/* 달력 다이얼로그 */}
                <Dialog
                    open={openDatePicker}
                    onClose={() => setOpenDatePicker(false)}
                    PaperProps={{
                        style: {
                            position: 'absolute',
                            bottom: 0,
                            height: '70%',
                            width: '100%',
                            margin: 0,
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            padding: '0 10px'
                        }
                    }}
                >
                    <DialogTitle sx={{ padding: '10px 24px' }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Typography>조회</Typography>
                            <IconButton
                                onClick={() => setOpenDatePicker(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{
                            width: '100%',
                            borderBottom: '1px solid #e0e0e0',
                            marginBottom: '10px'
                        }} />
                    </DialogTitle>
                    <DialogContent sx={{ paddingX: '2' }}>
                        <CustomCalendar value={selectedDate} onChange={handleDateChange} abnormalDates={abnormalDates} onMonthChange={fetchMonthAbnormalDates} />
                    </DialogContent>
                </Dialog>
            </Container>
        </Box>
    );
}

export default validationAuth(RegisteredDetail);